import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const DiscoveryDownload = () => {
  const { requestCode } = useParams();
  const [requestInfo, setRequestInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  // OTP flow (D1) — verification state is request-scoped on the server
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [zipPassword, setZipPassword] = useState(null);

  // Integrity verification box
  const [showVerify, setShowVerify] = useState(false);
  const [verifyHash, setVerifyHash] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);

  // Serving-firm branding arrives on the request-info response, resolved from
  // the request's own tenant — this page never calls /api/public/firm-info.
  const firmName = requestInfo?.firm_name || '';
  const firmEmail = requestInfo?.firm_email || '';
  const needsOtp = requestInfo?.mode === 'otp' && !verified && !requestInfo?.otp_verified;

  useEffect(() => {
    loadRequestInfo();
  }, [requestCode]);

  const loadRequestInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/discovery/download/${requestCode}`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Request not found');
      }
      const data = await response.json();
      setRequestInfo(data);
      setDownloaded(data.already_downloaded);
      if (data.otp_verified) setVerified(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    try {
      setOtpSending(true);
      setOtpError(null);
      const response = await fetch(`/api/discovery/download/${requestCode}/otp/request`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Could not send the code');
      setOtpSent(true);
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) return;
    try {
      setOtpVerifying(true);
      setOtpError(null);
      const response = await fetch(`/api/discovery/download/${requestCode}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: otpCode.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Verification failed');
      setVerified(true);
      setZipPassword(data.zip_password);
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await fetch(`/api/discovery/download/${requestCode}/file`, {
        method: 'POST',
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'discovery.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setDownloaded(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleVerifyHash = async () => {
    const h = verifyHash.trim().toLowerCase();
    if (!h) return;
    try {
      setVerifyBusy(true);
      setVerifyResult(null);
      const response = await fetch('/api/discovery/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_code: requestCode, hash: h }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Verification failed');
      setVerifyResult(data);
    } catch (err) {
      setVerifyResult({ error: err.message });
    } finally {
      setVerifyBusy(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-virgo-navy-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-virgo-navy-800 flex items-center justify-center p-4">
        <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-virgo-gold-400 mb-2">Link Unavailable</h1>
          <p className="text-virgo-teal-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-virgo-navy-800">
      {/* Header */}
      <div className="bg-blue-900 text-white py-6">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold">{firmName || 'Secure Discovery Delivery'}</h1>
          <p className="text-blue-200 mt-1">Secure Discovery Delivery</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-virgo-navy-700 rounded-lg shadow-lg overflow-hidden">
          {/* Case Info */}
          <div className="bg-virgo-navy-600 px-6 py-4 border-b">
            <p className="text-sm text-virgo-teal-400">Discovery Documents for</p>
            <h2 className="text-xl font-semibold text-virgo-gold-400">
              {requestInfo.case_name}
              {requestInfo.version > 1 && (
                <span className="ml-2 text-sm text-virgo-teal-400">(Amended — v{requestInfo.version})</span>
              )}
            </h2>
          </div>

          {/* Download Info */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-virgo-teal-400">Total Files</p>
                <p className="text-lg font-semibold">{requestInfo.total_files}</p>
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Total Size</p>
                <p className="text-lg font-semibold">{formatFileSize(requestInfo.total_size_bytes)}</p>
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Generated</p>
                <p className="text-lg font-semibold">
                  {requestInfo.generated_at ? new Date(requestInfo.generated_at).toLocaleDateString() : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Expires</p>
                <p className="text-lg font-semibold">
                  {requestInfo.expires_at ? new Date(requestInfo.expires_at).toLocaleDateString() : 'Never'}
                </p>
              </div>
            </div>

            {/* OTP verification (D1) */}
            {needsOtp ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start mb-3">
                  <span className="text-2xl mr-3">✉️</span>
                  <div>
                    <p className="font-semibold text-blue-800">Verify your email to download</p>
                    <p className="text-sm text-blue-700 mt-1">
                      We'll send a one-time code to the recipient address on file.
                      After verification, the archive password is shown here — no second email needed.
                    </p>
                  </div>
                </div>
                {!otpSent ? (
                  <button
                    onClick={handleRequestOtp}
                    disabled={otpSending}
                    className="w-full py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                  >
                    {otpSending ? 'Sending…' : 'Email me a verification code'}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder="Enter the 6-digit code"
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg text-center text-xl tracking-widest font-mono text-blue-900"
                      autoFocus
                    />
                    <button
                      onClick={handleVerifyOtp}
                      disabled={otpVerifying || !otpCode.trim()}
                      className="w-full py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                    >
                      {otpVerifying ? 'Verifying…' : 'Verify code'}
                    </button>
                    <button
                      onClick={handleRequestOtp}
                      disabled={otpSending}
                      className="w-full text-sm text-blue-700 hover:text-blue-900 py-1"
                    >
                      Resend code
                    </button>
                  </div>
                )}
                {otpError && <p className="text-sm text-red-600 mt-2">{otpError}</p>}
              </div>
            ) : (
              <>
                {requestInfo.mode === 'otp' && zipPassword && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-center">
                    <p className="text-sm text-green-800 font-semibold mb-1">Email verified ✓ — ZIP password:</p>
                    <p className="text-2xl font-mono tracking-wider text-green-900">{zipPassword}</p>
                    <p className="text-xs text-green-700 mt-2">
                      You'll need this password to open the downloaded archive. It is shown only here.
                    </p>
                  </div>
                )}
                {requestInfo.mode === 'password' && (
                  <div className={`${downloaded ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4 mb-6`}>
                    <div className="flex items-start">
                      <span className="text-2xl mr-3">{downloaded ? '✅' : '🔒'}</span>
                      <div>
                        <p className={`font-semibold ${downloaded ? 'text-green-800' : 'text-blue-800'}`}>
                          {downloaded ? 'Download Complete' : 'Secure Delivery'}
                        </p>
                        <p className={`text-sm mt-1 ${downloaded ? 'text-green-700' : 'text-blue-700'}`}>
                          {downloaded
                            ? 'A separate email containing the password to unlock the ZIP file has been sent to your email address.'
                            : 'After downloading, you will receive a separate email with the password to unlock the encrypted ZIP file.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors ${
                    downloading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-virgo-teal-600 hover:bg-virgo-teal-700 text-white'
                  }`}
                >
                  {downloading ? 'Preparing Download…' : downloaded ? 'Download Again' : 'Download Discovery Documents'}
                </button>
              </>
            )}

            {/* Integrity verification */}
            <div className="mt-6 border-t border-virgo-navy-600 pt-4">
              <button
                onClick={() => setShowVerify(!showVerify)}
                className="text-sm text-virgo-teal-400 hover:text-virgo-teal-300"
              >
                {showVerify ? '▾' : '▸'} Verify document integrity
              </button>
              {showVerify && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-virgo-teal-500">
                    Paste a SHA-256 hash (of a produced file, or the manifest hash from MANIFEST.pdf)
                    to confirm it matches this production exactly.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={verifyHash}
                      onChange={(e) => { setVerifyHash(e.target.value); setVerifyResult(null); }}
                      placeholder="SHA-256 hash (64 hex characters)"
                      className="flex-1 px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-sm font-mono text-white"
                    />
                    <button
                      onClick={handleVerifyHash}
                      disabled={verifyBusy || !verifyHash.trim()}
                      className="px-4 py-2 bg-virgo-teal-600 hover:bg-virgo-teal-700 text-white rounded-lg text-sm disabled:opacity-50"
                    >
                      {verifyBusy ? '…' : 'Verify'}
                    </button>
                  </div>
                  {verifyResult && (
                    verifyResult.error ? (
                      <p className="text-sm text-red-400">{verifyResult.error}</p>
                    ) : verifyResult.match ? (
                      <p className="text-sm text-green-400">
                        ✓ MATCH — {verifyResult.matched === 'manifest'
                          ? 'this is the authentic production manifest.'
                          : `authentic produced file: ${verifyResult.filename}`}
                      </p>
                    ) : (
                      <p className="text-sm text-red-400">
                        ✗ NO MATCH — this hash does not correspond to any file in this production.
                      </p>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-virgo-navy-600 px-6 py-4 border-t">
            <p className="text-xs text-virgo-teal-400 text-center">
              This is a secure document delivery from {firmName || 'the sending firm'}.
              If you did not expect this delivery, please contact the sender
              {firmEmail ? ` at ${firmEmail}` : ''}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryDownload;
